import { Box, IconButton, styled, TextField, Theme } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import PlayCircleOutlineOutlinedIcon from '@material-ui/icons/PlayCircleOutlineOutlined';
import AlertMaxSizeImage from '@src/components/ActiveChat/AlertMaxSizeImage';
import { activeChatSelector } from '@src/selectors/chats';
import type { RootState } from '@src/store/reduxstore';
import { ChatTray } from '@src/types/chats';
import { isImageTypeAllowed, isVideoTypeAllowed } from '@src/utils/mediaUtils';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { MediaType } from '../../../model/frontendmodel';
import ImageCaptionPreview from './MediaCaptionPreview';

export type MediaCaptionProps = {
  values: MediaCaptionValue[];
  onChangeValue: (value: MediaCaptionValue, index: number) => void;
  onRemoveValue: (value: MediaCaptionValue, index: number) => void;
  onCreateValue: (value: File) => void;
  onSendValue: () => void;
};
export type MediaCaptionValue = {
  image: string;
  caption?: string;
  type: MediaType.IMAGE | MediaType.VIDEO;
};

const MediaCaption: React.FC<MediaCaptionProps> = ({
  values,
  onChangeValue,
  onRemoveValue,
  onCreateValue,
  onSendValue,
}) => {
  const [captionValue, setCaptionValue] = useState('');
  const [activeIndexImg, setActiveIndexImg] = useState(0);
  const fileRef = useRef() as MutableRefObject<HTMLInputElement>;
  const inputRef = useRef() as MutableRefObject<HTMLInputElement>;

  useEffect(() => {
    if (values[activeIndexImg]) {
      values[activeIndexImg].caption = captionValue;
      onChangeValue(values[activeIndexImg], activeIndexImg);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captionValue]);

  useEffect(() => {
    setCaptionValue(values[activeIndexImg]?.caption ?? '');
    inputRef.current.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndexImg]);

  const removeIndexImage = (index: number) => {
    setActiveIndexImg(0);
    onChangeValue(values[0], 0);
    onRemoveValue(values[activeIndexImg], index);
  };

  const uploadMedia = ([file]: FileList) => {
    if (isImageTypeAllowed(file.type) || isVideoTypeAllowed(file.type))
      onCreateValue(file as File);
  };

  const activeChat = useSelector(activeChatSelector) as ChatTray;
  const activeProviderName = activeChat?.provider;
  const loadedImagesErrors = useSelector(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    (state: RootState) => state.errors.loadedImagesErrors || []
  );

  return (
    <>
      <Box display="flex" flexDirection="column" width={700}>
        <MediaBackdrop
          style={{
            minHeight: 350,
          }}
        >
          {values[activeIndexImg]?.image?.length && (
            <ImageCaptionPreview
              src={values[activeIndexImg].image}
              type={values[activeIndexImg].type}
            />
          )}
        </MediaBackdrop>

        {values[activeIndexImg] && (
          <Box my={2} component="form" onSubmit={onSendValue}>
            <TextField
              fullWidth
              autoFocus
              inputRef={inputRef}
              color="primary"
              variant="outlined"
              value={captionValue}
              data-testid="caption-input"
              onChange={({ target }) => setCaptionValue(target.value)}
              placeholder="Escribe un mensaje"
            />
          </Box>
        )}

        {loadedImagesErrors && (
          <Box mb={2}>
            <AlertMaxSizeImage
              activeProviderName={activeProviderName}
              loadedImagesErrors={loadedImagesErrors}
            />
          </Box>
        )}
        <MediaThumbnailContainer>
          {values.map(({ image, type }, index) => (
            <MediaThumbnail
              key={image}
              active={(index === activeIndexImg).toString()}
              onClick={() => setActiveIndexImg(index)}
            >
              <IconButton
                data-testid="remove-image"
                onClick={e => {
                  e.stopPropagation();
                  removeIndexImage(index);
                }}
              >
                <CloseIcon />
              </IconButton>
              {type === MediaType.VIDEO && (
                <Box
                  component={PlayCircleOutlineOutlinedIcon}
                  className="video-icon"
                />
              )}
              <ImageCaptionPreview src={image} type={type} thumbnail />
            </MediaThumbnail>
          ))}
          <input
            type="file"
            ref={fileRef}
            data-testid="upload-file"
            style={{ display: 'none' }}
            onChange={({ target }) =>
              target?.files && uploadMedia(target.files)
            }
            accept="image/png,image/jpeg,video/mp4"
          />

          <MediaThumbnail onClick={() => fileRef.current.click()}>
            <MediaBackdrop>
              <AddIcon />
            </MediaBackdrop>
          </MediaThumbnail>
        </MediaThumbnailContainer>
      </Box>
    </>
  );
};

const MediaThumbnailContainer = styled('div')(() => ({
  justifyContent: 'center',
  margin: '0 -5px',
  display: 'flex',
}));

const MediaThumbnail = styled('div')(
  ({ theme, active }: { theme: Theme; active?: string }) => ({
    backgroundColor: theme.palette.grey[400],
    outline: active ? `2px solid ${theme.palette.primary.main}` : 'none',
    boxSizing: 'content-box',
    overflow: 'hidden',
    cursor: 'pointer',
    borderRadius: 5,
    margin: '0 5px',
    height: 65,
    width: 65,
    position: 'relative',

    '& .video-icon': {
      position: 'absolute',
      height: 26,
      width: 26,
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      color: '#ffff',
    },

    '& button': {
      padding: 0,
      fontSize: 15,
      position: 'absolute',
      right: 3,
      top: 3,
      zIndex: 10,

      '& svg': {
        fontSize: 'inherit',
        color: theme.palette.grey[100],
      },
    },
  })
);

const MediaBackdrop = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  backgroundColor: theme.palette.grey[400],
  color: theme.palette.grey[100],
  borderRadius: 5,
  overflowX: 'hidden',
}));

export default MediaCaption;
